import React, { memo } from "react";
import { buildAnimationCSS, animationSignature, buildGenerationCSS } from "./animation";
import { svgPropsFor } from "./svgAttrs";
import { fillRef, filterRef, EffectDefs } from "./effects";
import { bboxOf, centerOf, rotationOf } from "./canvasUtils";
import { resolveCornerRadii, roundedRectPath, hasCornerRadius, roundedPolygonPath, parsePoints, resolveCornerRadius } from "./cornerRadius";
import { isArrow, splitArrowPath, isFilledDeco } from "./lineEdit";

function renderShape(el, common) {
  switch (el.type) {
    case "rect":
      return <rect {...common} />;
    case "circle":
      return <circle {...common} />;
    case "ellipse":
      return <ellipse {...common} />;
    case "line":
      return <line {...common} />;
    case "polygon": {
      // When a corner radius is set, render the polygon as a <path> with
      // rounded corners instead of a plain <polygon>.
      if (hasCornerRadius(el.attrs) && el.attrs.points) {
        const pts = parsePoints(el.attrs.points);
        const bb = bboxOf(el);
        const r = resolveCornerRadius(el.attrs, bb.w, bb.h);
        if (r > 0) {
          const d = roundedPolygonPath(pts, r);
          const { points, ...rest } = common;
          return <path {...rest} d={d} />;
        }
      }
      return <polygon {...common} />;
    }
    case "polyline":
      return <polyline {...common} />;
    case "path":
      return <path {...common} />;
    case "text":
      return <text {...common}>{el.attrs.text}</text>;
    case "image":
      return <image {...common} />;
    default:
      return null;
  }
}

const ElementNode = memo(function ElementNode({ el, selected, onPointerDown, onDoubleClick, generating, cropping }) {
  // When `generating`, play the one-shot intro effect (typewriter / draw /
  // scale) triggered by the Play / Replay buttons, instead of the configured
  // animation.
  const at = el.animation?.type;
  const needsBox = generating || !!rotationOf(el) || at === "drawOn";
  const box = needsBox ? bboxOf(el) : null;
  let gen = null;
  let animCSS = null;
  let className = "";
  if (generating) {
    gen = buildGenerationCSS(el, box);
    className = gen ? gen.className : "";
  } else {
    animCSS = buildAnimationCSS(el, box);
    className = animCSS ? animCSS.className : "";
  }

  const safeProps = svgPropsFor(el.type, el.attrs);
  const fr = fillRef(el);
  if (fr) safeProps.fill = fr;
  const flt = filterRef(el);

  const common = {
    ...safeProps,
    className,
    onPointerDown: (e) => onPointerDown && onPointerDown(e, el),
    onDoubleClick: (e) => onDoubleClick && onDoubleClick(e, el),
    style: { cursor: "move" },
  };
  if (flt) common.filter = flt;

  // Image corner radius + crop: applied via a per-element <clipPath>.
  //
  // Data model: x/y/width/height is the CROPPED display box (the selection
  // bbox / Transform values). origX/origY/origW/origH is the full source
  // image box. The <image> tag is placed at the orig box so the complete
  // source is available, and a clipPath restricts the visible region to the
  // cropped box (with per-corner radii via a <path>). When there is no crop
  // the orig box equals the display box, so only the corner radius applies.
  //
  // The clipPath is ALWAYS applied (even in crop mode) so the image doesn't
  // flash when exiting crop mode. In crop mode the ImageCropOverlay renders a
  // separate semi-transparent copy of the full source image as a reference.
  //
  // rect: when a corner radius is set, the rect is clipped by a rounded-rect
  // <path> (per-corner radii) so it renders identically to an image with the
  // same radius — native <rect rx> only supports a single uniform radius and
  // cannot do per-corner rounding.
  let clipExtras = null;
  if (el.type === "image") {
    // Place the <image> at the original (uncropped) source box.
    const ox = el.attrs.origX ?? el.attrs.x ?? 0;
    const oy = el.attrs.origY ?? el.attrs.y ?? 0;
    const ow = el.attrs.origW ?? el.attrs.width ?? 0;
    const oh = el.attrs.origH ?? el.attrs.height ?? 0;
    common.x = ox;
    common.y = oy;
    common.width = ow;
    common.height = oh;

    // Cropped display box = the visible region (also the selection bbox).
    const cx = el.attrs.x ?? 0;
    const cy = el.attrs.y ?? 0;
    const cw = el.attrs.width ?? 0;
    const ch = el.attrs.height ?? 0;
    const hasCrop = ox !== cx || oy !== cy || ow !== cw || oh !== ch;
    if (hasCornerRadius(el.attrs) || hasCrop) {
      const { rTL, rTR, rBR, rBL } = resolveCornerRadii(el.attrs, cw, ch);
      const d = roundedRectPath(cx, cy, cw, ch, rTL, rTR, rBR, rBL);
      const clipId = `clip-${el.id}`;
      common.clipPath = `url(#${clipId})`;
      clipExtras = (
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <path d={d} />
        </clipPath>
      );
    }
  } else if (el.type === "rect" && hasCornerRadius(el.attrs)) {
    // Rect with corner radius: clip via a rounded-rect path (per-corner).
    const cx = el.attrs.x ?? 0;
    const cy = el.attrs.y ?? 0;
    const cw = el.attrs.width ?? 0;
    const ch = el.attrs.height ?? 0;
    const { rTL, rTR, rBR, rBL } = resolveCornerRadii(el.attrs, cw, ch);
    const d = roundedRectPath(cx, cy, cw, ch, rTL, rTR, rBR, rBL);
    const clipId = `clip-${el.id}`;
    common.clipPath = `url(#${clipId})`;
    // Native rx/ry would conflict with the clipPath rounding — drop them so
    // the <rect> stays a plain rectangle and only the clip shapes the corners.
    delete common.rx;
    delete common.ry;
    clipExtras = (
      <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
        <path d={d} />
      </clipPath>
    );
  }

  // Static rotation: pivot is always recomputed from the current bbox center,
  // so rotation stays consistent across move / resize without pivot drift.
  // For images, horizontal/vertical flip is merged into the same transform
  // (scale around the bbox center) so flip + rotation compose correctly.
  const rot = rotationOf(el);
  const flipH = el.type === "image" && Number(el.attrs.flipH) === -1 ? -1 : 1;
  const flipV = el.type === "image" && Number(el.attrs.flipV) === -1 ? -1 : 1;
  if (rot || flipH === -1 || flipV === -1) {
    const c = centerOf(box || bboxOf(el));
    const parts = [];
    if (flipH === -1 || flipV === -1) {
      parts.push(`translate(${c.x} ${c.y}) scale(${flipH} ${flipV}) translate(${-c.x} ${-c.y})`);
    }
    if (rot) parts.push(`rotate(${rot} ${c.x} ${c.y})`);
    common.transform = parts.join(" ");
  }
  // Stroke-draw animations rely on a normalized path length so the reveal runs
  // accurately from start to end regardless of the real geometry length.
  if (!generating && (at === "draw" || at === "drawReverse")) common.pathLength = 1;
  if (!generating && animCSS?.pathLength) common.pathLength = 1;
  if (generating && gen?.pathLength) common.pathLength = 1;

  // Arrow elements render as THREE independent <path> elements: the main
  // stroke, the start decoration, and the end decoration. The three parts are
  // fully independent (each starts with its own M) to avoid fill-rule
  // bleeding or unintended sub-path closure that distorts the shape.
  //
  // During draw animations:
  //   • main stroke  — stroke-dasharray + stroke-dashoffset (line grows)
  //   • start deco   — fixed at start point, no animation (always visible)
  //   • end deco     — travels along the main stroke via offset-path +
  //                    offset-distance 0%→100% (follows the growing tip,
  //                    like dragging to draw)
  // Arrow decorations ALWAYS use offset-path + offset-rotate:auto (in every
  // state: static, animated, generating) so they follow the stroke's tangent —
  // including on curved paths where the tangent changes along the way.
  //
  // Arrow elements are a SINGLE graphic composed of multiple parts (main
  // stroke + start/end decorations). An animation must apply to the WHOLE
  // arrow, not to the parts individually.
  //
  // Two rendering modes:
  //   • WHOLE mode (static + non-draw animations): wrap all parts in a <g>
  //     carrying the animation class. Decorations use ABSOLUTE coords
  //     (startDecoD / endDecoD) so they render in place at the endpoints.
  //     The entire arrow rotates/scales/fades/moves together as one object.
  //   • SPLIT mode (draw / drawReverse only): the "draw on" effect requires
  //     the end decoration to travel along the growing stroke, so the parts
  //     are split. Decorations use RELATIVE coords + offset-path. This is the
  //     only case where parts are animated independently.
  const arrowSplit = isArrow(el) ? splitArrowPath(el) : null;
  // draw / drawReverse need the split (decoration travels along the stroke).
  // generating (Play/Replay) draw mode also needs the split. Everything else
  // treats the arrow as a whole.
  const arrowIsSplit =
    arrowSplit &&
    ((generating && gen?.pathLength) ||
      (!generating && animCSS && (at === "draw" || at === "drawReverse")));
  // Start decoration class (only used in SPLIT mode)
  const arrowStartCls = arrowIsSplit
    ? (generating
        ? (gen?.arrowStartCls || null)
        : (animCSS?.arrowStartCls || null))
    : null;
  // Filled decoration types (round, triangle, circle, diamond, etc.) render
  // as solid shapes: fill with the stroke color. line-arrow and square stay
  // as open stroked shapes (no fill).
  const startFill = arrowSplit && isFilledDeco(arrowSplit.sType) ? (safeProps.stroke || "#000") : "none";
  const arrowStartProps = arrowSplit
    ? { ...common, className: arrowStartCls || undefined, fill: startFill, pathLength: undefined, strokeDasharray: undefined, strokeDashoffset: undefined }
    : null;
  // End decoration class (only used in SPLIT mode)
  const arrowEndCls = arrowIsSplit
    ? (generating
        ? gen?.arrowEndCls
        : (animCSS?.arrowEndCls || null))
    : null;
  const endFill = arrowSplit && isFilledDeco(arrowSplit.eType) ? (safeProps.stroke || "#000") : "none";
  const arrowEndProps = arrowSplit
    ? { ...common, className: arrowEndCls || undefined, fill: endFill, pathLength: undefined, strokeDasharray: undefined, strokeDashoffset: undefined }
    : null;

  // WHOLE mode: the <g> carries the animation class so the whole arrow
  // animates together. The inner paths use absolute coords and carry NO
  // animation class (className undefined). Pointer handlers stay on the <g>.
  const arrowWholeGroupCls = arrowSplit && !arrowIsSplit ? className : null;
  const arrowWholeInner = arrowSplit && !arrowIsSplit
    ? { ...common, className: undefined, onPointerDown: undefined, onDoubleClick: undefined }
    : null;

  // Scoped <style> + <clipPath> for the active generation effect. Mounts and
  // unmounts with the node (the node is re-keyed when generation toggles), so
  // the animation restarts cleanly and the normal render resumes afterwards.
  const genExtras = generating && gen ? (
    <>
      {gen.clip && (
        <clipPath id={gen.clip.id} clipPathUnits="userSpaceOnUse">
          <rect className={gen.clip.cls} x={gen.clip.x} y={gen.clip.y} width={gen.clip.w} height={gen.clip.h} />
        </clipPath>
      )}
      <style>{gen.css}</style>
    </>
  ) : null;

  // Clip element for the configurable "Draw On" reveal (typewriter / wipe).
  // The animation CSS itself is emitted once by <AnimationStyles>; here we only
  // mount the per-element <clipPath> with the animated sweeping rect.
  const animExtras = !generating && animCSS?.clip ? (
    <clipPath id={animCSS.clip.id} clipPathUnits="userSpaceOnUse">
      <rect className={animCSS.clip.cls} x={animCSS.clip.x} y={animCSS.clip.y} width={animCSS.clip.w} height={animCSS.clip.h} />
    </clipPath>
  ) : null;

  // Thin open shapes (line / polyline / unfilled path) have a tiny clickable
  // band, which makes selecting them — and double-clicking to insert an edit
  // point — frustrating. Render an invisible wide-stroke proxy on top that
  // carries the pointer handlers, so the *whole length* of the line is an easy
  // hit target without changing how it looks or exports.
  const isOpenStroke =
    el.type === "line" ||
    el.type === "polyline" ||
    (el.type === "path" && (el.attrs?.fill == null || el.attrs.fill === "none"));

  if (isOpenStroke && (onPointerDown || onDoubleClick)) {
    const HIT_WIDTH = 16;
    const hitProps = {
      ...safeProps,
      fill: "none",
      stroke: "transparent",
      strokeWidth: Math.max(Number(safeProps.strokeWidth) || 0, HIT_WIDTH),
      strokeDasharray: undefined,
      strokeLinecap: "round",
      pointerEvents: "stroke",
      onPointerDown: common.onPointerDown,
      onDoubleClick: common.onDoubleClick,
      style: { cursor: "move" },
    };
    if (common.transform) hitProps.transform = common.transform;
    if (common.pathLength) delete hitProps.pathLength;
    return (
      <>
        <EffectDefs el={el} />
        {genExtras}
        {animExtras}
        {clipExtras}
        {arrowSplit ? (
          arrowIsSplit ? (
            <>
              <path {...common} d={arrowSplit.mainD} />
              {arrowSplit.startDecoD && <path {...arrowStartProps} d={arrowSplit.startDecoD} />}
              {arrowSplit.endDecoD && <path {...arrowEndProps} d={arrowSplit.endDecoDRelative} />}
            </>
          ) : (
            <g className={arrowWholeGroupCls || undefined} onPointerDown={common.onPointerDown} onDoubleClick={common.onDoubleClick} style={{ cursor: "move" }}>
              <path {...arrowWholeInner} d={arrowSplit.mainD} />
              {arrowSplit.startDecoD && <path {...arrowWholeInner} fill={startFill} d={arrowSplit.startDecoD} />}
              {arrowSplit.endDecoD && <path {...arrowWholeInner} fill={endFill} d={arrowSplit.endDecoD} />}
            </g>
          )
        ) : renderShape(el, common)}
        {renderShape(el, hitProps)}
      </>
    );
  }

  return (
    <>
      <EffectDefs el={el} />
      {genExtras}
      {animExtras}
      {clipExtras}
      {arrowSplit ? (
        arrowIsSplit ? (
          <>
            <path {...common} d={arrowSplit.mainD} />
            {arrowSplit.startDecoD && <path {...arrowStartProps} d={arrowSplit.startDecoD} />}
            {arrowSplit.endDecoD && <path {...arrowEndProps} d={arrowSplit.endDecoDRelative} />}
          </>
        ) : (
          <g className={arrowWholeGroupCls || undefined} onPointerDown={common.onPointerDown} onDoubleClick={common.onDoubleClick} style={{ cursor: "move" }}>
            <path {...arrowWholeInner} d={arrowSplit.mainD} />
            {arrowSplit.startDecoD && <path {...arrowWholeInner} fill={startFill} d={arrowSplit.startDecoD} />}
            {arrowSplit.endDecoD && <path {...arrowWholeInner} fill={endFill} d={arrowSplit.endDecoD} />}
          </g>
        )
      ) : renderShape(el, common)}
    </>
  );
});

// Animation <style> block — only re-renders when animation configs or arrow
// path data change, so dragging (position-only changes) never restarts
// animations (no flicker). Also emits offset-path CSS for static (no-animation)
// arrows so their decorations follow the stroke tangent on curved paths.
const AnimationStyles = memo(
  function AnimationStyles({ elements }) {
    const css = elements
      .map((el) => {
        // Animated elements: full animation CSS (includes arrow offset-path
        // for draw/drawReverse only; other arrow animations wrap the whole
        // arrow in a <g> so no per-decoration CSS is needed).
        const anim = buildAnimationCSS(el, el.animation?.type === "drawOn" ? bboxOf(el) : null);
        if (anim) return anim.css;
        // Static arrows now render as a whole <g> with absolute-coord
        // decorations — no offset-path CSS needed.
        return null;
      })
      .filter(Boolean)
      .join("\n");
    if (!css) return null;
    return (
      <defs>
        <style>{css}</style>
      </defs>
    );
  },
  (prev, next) => animationSignature(prev.elements) === animationSignature(next.elements)
);

export function ElementsLayer({ elements, selectedId, onPointerDown, onDoubleClick, replayNonce, generatingIds, croppingId }) {
  return (
    <>
      <AnimationStyles elements={elements} />
      {elements.map((el) => {
        const generating = generatingIds ? generatingIds.has(el.id) : false;
        return (
          <ElementNode
            key={`${el.id}:${(replayNonce && replayNonce[el.id]) || 0}:${generating ? 1 : 0}`}
            el={el}
            selected={selectedId === el.id}
            onPointerDown={onPointerDown}
            onDoubleClick={onDoubleClick}
            generating={generating}
            cropping={croppingId === el.id}
          />
        );
      })}
    </>
  );
}
